import { useEffect, useRef } from 'react';

/**
 * Declarative setInterval — re-runs `callback` every `delayMs`. Pass `null`
 * to pause. The callback ref is updated on every render so closures stay
 * fresh without resetting the timer.
 *
 * Why this and not a bare useEffect: a fresh callback every render would
 * either need to re-create the interval (causing drift on each render) or
 * close over stale state. The ref trick keeps a single interval running
 * with always-current behaviour. Pattern from Dan Abramov's `useInterval`.
 */
export function useInterval(callback: () => void, delayMs: number | null): void {
  const savedCallback = useRef<() => void>(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delayMs === null) return;
    const id = setInterval(() => {
      savedCallback.current();
    }, delayMs);
    return () => clearInterval(id);
  }, [delayMs]);
}
